import React from "react";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";

export function ShippingTextDialog({
  open,
  onClose,
  modalPaperSx,
  primary,
  shippingTextSaving,
  shippingTextDraft,
  setShippingTextDraft,
  saveShippingText,
  shippingTextDirty,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>Shipping Text</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        <Typography variant="body2" sx={{ color: "#64748b", mb: 1.25 }}>
          This text is shown on the shipping agreement page for this org. It is separate from your email template body.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={10}
          label="Shipping agreement text"
          value={shippingTextDraft}
          onChange={(e) => setShippingTextDraft(e.target.value)}
          disabled={shippingTextSaving}
          sx={{ backgroundColor: "#fff", borderRadius: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={shippingTextSaving}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Close
        </Button>
        <Button
          onClick={saveShippingText}
          disabled={shippingTextSaving || !shippingTextDirty}
          startIcon={shippingTextSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon />}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            color: "#fff",
            background: "linear-gradient(90deg,#0ea5e9,#0284c7)",
            "&:hover": { background: "linear-gradient(90deg,#0284c7,#0369a1)" },
          }}
        >
          {shippingTextSaving ? "Saving..." : "Save Shipping Text"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RegistrationFulfillmentConfirmDialog({
  open,
  onClose,
  onConfirm,
  modalPaperSx,
  primary,
  registrationFulfillmentConfirm,
  registrationFulfillmentConfirmBusy,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        Confirm Update
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        <Typography sx={{ color: "#111827", fontWeight: 800, lineHeight: 1.6 }}>
          {registrationFulfillmentConfirm.type === "pickup"
            ? registrationFulfillmentConfirm.nextValue
              ? `Are you sure you want to mark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will mark the pickup as complete.`
              : `Are you sure you want to unmark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will reopen the pickup status.`
            : registrationFulfillmentConfirm.type === "delivery"
              ? registrationFulfillmentConfirm.nextValue
                ? `Are you sure you want to mark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will mark the delivery as complete.`
                : `Are you sure you want to unmark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will reopen the delivery status.`
            : registrationFulfillmentConfirm.nextValue
              ? `Are you sure you want to mark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will mark the shipment as complete.`
              : `Are you sure you want to unmark fulfilled for ${registrationFulfillmentConfirm.userName || "this user"}? This will reopen the shipment status.`}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={registrationFulfillmentConfirmBusy}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={registrationFulfillmentConfirmBusy}
          startIcon={registrationFulfillmentConfirmBusy ? <CircularProgress size={16} /> : null}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            color: "#fff",
            background: "linear-gradient(90deg,#16a34a,#15803d)",
            "&:hover": { background: "linear-gradient(90deg,#15803d,#166534)" },
          }}
        >
          {registrationFulfillmentConfirmBusy ? "Confirming..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AuctionUserDetailsDialog({
  open,
  onClose,
  modalPaperSx,
  primary,
  auctionUserDetailsBusy,
  auctionUserDetailsErr,
  auctionUserDetailsData,
  auctionUserDetailsSourceReg,
  auctionUserDetailsEmail,
  auctionUserDetailsForm,
  auctionUserDetailsSaveError,
  auctionUserDetailsSavingByField,
  setAuctionUserDetailsField,
  flushAuctionUserDetailsFieldSave,
  NamedUrlsEditorComponent,
  normalize,
  buildAuctionUserDetailsEditableForm,
  getFirstLastNameParts,
  normalizeTierKey,
  getEffectiveTier,
  normalizeCustomerTypeKey,
  getCustomerType,
  normalizeAccountManagerId,
  getResolvedAccountManagerIdForUser,
  getAccountManagerSelectOptionsForUser,
  getResolvedAccountManagerNameForUser,
  getPickupLocationSelectOptions,
  CUSTOMER_TYPE_OPTIONS,
  HIBID_TIER_OPTIONS,
  isEmail,
  getLcvEstimate,
  getRegsCount,
  getBidsCount,
  getHibidBids90Count,
  getRegs12mCount,
  toAbsInt,
  getRollingWin,
  getLotsWonFromWin,
  getAvgHammerFromWin,
  getBidIntensity90,
  getBpi90,
  moneyPlain,
  formatNumber2,
  safeNum,
  fmtDate,
  customerTypeLabel,
}) {
  const row = {
    ...(auctionUserDetailsSourceReg || {}),
    ...(auctionUserDetailsData || {}),
  };
  const fullName = String(
    auctionUserDetailsForm?.bidderName ||
      row?.bidderName ||
      auctionUserDetailsForm?.username ||
      row?.username ||
      auctionUserDetailsEmail ||
      "User"
  ).trim();

  const email = String(row?.email || row?.id || auctionUserDetailsEmail || "").trim().toLowerCase();
  const formState =
    auctionUserDetailsForm && normalize(auctionUserDetailsForm?.email || "") === normalize(email)
      ? auctionUserDetailsForm
      : buildAuctionUserDetailsEditableForm(row, email);
  const bidderName = String(formState?.bidderName || row?.bidderName || auctionUserDetailsSourceReg?.bidderName || "").trim();
  const username = String(formState?.username || row?.username || auctionUserDetailsSourceReg?.username || "").trim();
  const company = String(formState?.company || row?.company || row?.companyName || auctionUserDetailsSourceReg?.company || "").trim();
  const phone = String(formState?.phone || row?.phone || auctionUserDetailsSourceReg?.phone || "").trim();
  const address = String(formState?.address || row?.address || row?.billToAddress1 || "").trim();
  const city = String(row?.billToCity || "").trim();
  const state = String(row?.billToState || "").trim();
  const zip = String(row?.zip || row?.billToZip || "").trim();
  const { firstName, lastName } = getFirstLastNameParts(bidderName || username || email);
  const tier = normalizeTierKey(formState?.tier || getEffectiveTier(row));
  const type = normalizeCustomerTypeKey(formState?.customerType || getCustomerType(row));
  const managerResolveRow = {
    ...row,
    buyerTierManual: tier || row?.buyerTierManual || row?.buyerTier || row?.tier || row?.derived?.tier || "",
  };
  const accountManagerId = normalizeAccountManagerId(
    formState?.accountManagerId || getResolvedAccountManagerIdForUser(managerResolveRow) || ""
  );
  const managerOptions = getAccountManagerSelectOptionsForUser({
    ...managerResolveRow,
    accountManagerId: accountManagerId || null,
  });
  const accountManagerName = getResolvedAccountManagerNameForUser({
    ...managerResolveRow,
    accountManagerId: accountManagerId || null,
  });
  const defaultPickupLocationName = String(
    formState?.defaultPickupLocationName ||
    row?.defaultPickupLocationName ||
    auctionUserDetailsSourceReg?.defaultPickupLocationName ||
    ""
  ).trim();
  const pickupLocationOptions = getPickupLocationSelectOptions([defaultPickupLocationName || ""]);
  const typeOptions =
    type && !CUSTOMER_TYPE_OPTIONS.includes(type)
      ? [...CUSTOMER_TYPE_OPTIONS, type]
      : CUSTOMER_TYPE_OPTIONS;
  const tierOptions =
    tier && !HIBID_TIER_OPTIONS.includes(tier)
      ? [...HIBID_TIER_OPTIONS, tier]
      : HIBID_TIER_OPTIONS;
  const hasAutoSaveInFlight = Object.keys(auctionUserDetailsSavingByField || {}).length > 0;
  const editDisabled = !isEmail(email);
  const lcv = getLcvEstimate(row);
  const regsCount = getRegsCount(row);
  const bidsCount = getBidsCount(row);
  const hibidBids90Count = getHibidBids90Count(row);
  const regs12mCount = getRegs12mCount(row);
  const bids12mCount = toAbsInt(
    row?.totalUniqueBidCountLast12MonthsValue ??
      row?.totalUniqueBidCountLast12Months ??
      row?.bids12m ??
      0
  );
  const win90 = getRollingWin(row, "win90");
  const lots90 = getLotsWonFromWin(win90);
  const avg90 = getAvgHammerFromWin(win90);
  const bidIntensity90 = getBidIntensity90(row);
  const bpi90 = getBpi90(row);
  const addressLine =
    address ||
    [String(row?.billToAddress1 || "").trim(), city, state, zip].filter(Boolean).join(", ");
  const statsRows = [
    { label: "LCV", value: lcv != null ? `$${moneyPlain(lcv)}` : "—" },
    { label: "Regs", value: formatNumber2(regsCount) },
    { label: "My Bids", value: formatNumber2(bidsCount) },
    { label: "HiBid Bids 90d", value: formatNumber2(hibidBids90Count) },
    { label: "Regs 12m", value: formatNumber2(regs12mCount) },
    { label: "Bids 12m", value: formatNumber2(bids12mCount) },
    { label: "Lots 90d", value: lots90 != null ? formatNumber2(lots90) : "—" },
    { label: "Avg Hammer 90d", value: avg90 != null ? `$${moneyPlain(avg90)}` : "—" },
    { label: "Bid Intensity 90d", value: safeNum(bidIntensity90).toFixed(2) },
    { label: "BPI 90d", value: safeNum(bpi90).toFixed(2) },
    { label: "Last Email", value: fmtDate(row?.lastEmailSentAt) },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        {`User Details • ${fullName || "User"}`}
        <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
          {auctionUserDetailsEmail || "—"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        {auctionUserDetailsBusy ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress />
          </Box>
        ) : null}

        {auctionUserDetailsErr ? (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5 }}>
            {auctionUserDetailsErr}
          </Alert>
        ) : null}

        {!auctionUserDetailsBusy && !auctionUserDetailsErr ? (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(229,231,235,0.95)", background: "#fff" }}>
              <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 900, textTransform: "uppercase" }}>
                Profile
              </Typography>
              <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={hasAutoSaveInFlight ? "Auto-saving..." : "Auto-save enabled"}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  color={hasAutoSaveInFlight ? "primary" : "default"}
                />
                <Chip
                  size="small"
                  label={`Type: ${customerTypeLabel(type || "unclassified")}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Tier: ${tier || "—"}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Manager: ${accountManagerName || "—"}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  variant="outlined"
                />
              </Box>
              {auctionUserDetailsSaveError ? (
                <Alert severity="error" sx={{ mt: 0.8, borderRadius: 1.5 }}>
                  {auctionUserDetailsSaveError}
                </Alert>
              ) : null}
              <Box sx={{ mt: 0.9, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                <TextField
                  label="Bidder Name"
                  size="small"
                  value={bidderName}
                  onChange={(e) => setAuctionUserDetailsField("bidderName", e.target.value)}
                  onBlur={(e) => flushAuctionUserDetailsFieldSave("bidderName", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      flushAuctionUserDetailsFieldSave("bidderName", e.currentTarget.value);
                    }
                  }}
                  disabled={editDisabled}
                  fullWidth
                  sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" }, backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <Typography sx={{ color: "#334155" }}><b>First Name:</b> {firstName || "—"}</Typography>
                <Typography sx={{ color: "#334155" }}><b>Last Name:</b> {lastName || "—"}</Typography>
                <TextField
                  label="Username"
                  size="small"
                  value={username}
                  onChange={(e) => setAuctionUserDetailsField("username", e.target.value)}
                  onBlur={(e) => flushAuctionUserDetailsFieldSave("username", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      flushAuctionUserDetailsFieldSave("username", e.currentTarget.value);
                    }
                  }}
                  disabled={editDisabled}
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <TextField
                  label="Email"
                  size="small"
                  value={email || "—"}
                  disabled
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <TextField
                  label="Company"
                  size="small"
                  value={company}
                  onChange={(e) => setAuctionUserDetailsField("company", e.target.value)}
                  onBlur={(e) => flushAuctionUserDetailsFieldSave("company", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      flushAuctionUserDetailsFieldSave("company", e.currentTarget.value);
                    }
                  }}
                  disabled={editDisabled}
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <TextField
                  label="Phone"
                  size="small"
                  value={phone}
                  onChange={(e) => setAuctionUserDetailsField("phone", e.target.value)}
                  onBlur={(e) => flushAuctionUserDetailsFieldSave("phone", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      flushAuctionUserDetailsFieldSave("phone", e.currentTarget.value);
                    }
                  }}
                  disabled={editDisabled}
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <TextField
                  label="Address"
                  size="small"
                  value={addressLine || ""}
                  onChange={(e) => setAuctionUserDetailsField("address", e.target.value)}
                  onBlur={(e) => flushAuctionUserDetailsFieldSave("address", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      flushAuctionUserDetailsFieldSave("address", e.currentTarget.value);
                    }
                  }}
                  disabled={editDisabled}
                  fullWidth
                  sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" }, backgroundColor: "#fff", borderRadius: 1.5 }}
                />
                <FormControl size="small" fullWidth sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}>
                  <InputLabel id="auction-user-details-default-pickup-location-label">Default Location</InputLabel>
                  <Select
                    labelId="auction-user-details-default-pickup-location-label"
                    label="Default Location"
                    value={defaultPickupLocationName}
                    disabled={editDisabled}
                    onChange={(e) => setAuctionUserDetailsField("defaultPickupLocationName", e.target.value, { saveNow: true })}
                    sx={{ borderRadius: 1.5, backgroundColor: "#fff" }}
                  >
                    <MenuItem value="">
                      <em>No default location</em>
                    </MenuItem>
                    {pickupLocationOptions.map((option) => (
                      <MenuItem key={`auction-user-default-location-${option}`} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <NamedUrlsEditorComponent
                  rows={formState?.namedUrls || row?.namedUrls || []}
                  onChange={(next) => setAuctionUserDetailsField("namedUrls", next)}
                  onCommit={(next) => flushAuctionUserDetailsFieldSave("namedUrls", next)}
                  disabled={editDisabled}
                  title="Named URLs"
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(229,231,235,0.95)", background: "#fff" }}>
              <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 900, textTransform: "uppercase" }}>
                Stats
              </Typography>
              <Box sx={{ mt: 0.9, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="auction-user-details-type-label">Customer Type</InputLabel>
                  <Select
                    labelId="auction-user-details-type-label"
                    label="Customer Type"
                    value={String(type || "")}
                    disabled={editDisabled}
                    onChange={(e) => setAuctionUserDetailsField("customerType", e.target.value, { saveNow: true })}
                    sx={{ borderRadius: 1.5, backgroundColor: "#fff" }}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {typeOptions.map((opt) => (
                      <MenuItem key={`auction-user-type-${opt}`} value={opt}>
                        {customerTypeLabel(opt)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="auction-user-details-tier-label">Tier</InputLabel>
                  <Select
                    labelId="auction-user-details-tier-label"
                    label="Tier"
                    value={String(tier || "")}
                    disabled={editDisabled}
                    onChange={(e) => setAuctionUserDetailsField("tier", e.target.value, { saveNow: true })}
                    sx={{ borderRadius: 1.5, backgroundColor: "#fff" }}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {tierOptions.map((opt) => (
                      <MenuItem key={`auction-user-tier-${opt}`} value={opt}>
                        {String(opt || "").trim().charAt(0).toUpperCase() + String(opt || "").trim().slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="auction-user-details-manager-label">Account Manager</InputLabel>
                  <Select
                    labelId="auction-user-details-manager-label"
                    label="Account Manager"
                    value={String(accountManagerId || "")}
                    disabled={editDisabled}
                    onChange={(e) => setAuctionUserDetailsField("accountManagerId", e.target.value, { saveNow: true })}
                    sx={{ borderRadius: 1.5, backgroundColor: "#fff" }}
                  >
                    <MenuItem value="">
                      <em>Select Account Manager</em>
                    </MenuItem>
                    {managerOptions.map((opt) => (
                      <MenuItem key={`auction-user-manager-${opt.id}`} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 1.5,
                  border: "1px solid rgba(226,232,240,0.9)",
                  overflow: "hidden",
                }}
              >
                {statsRows.map((item, idx) => (
                  <Box
                    key={`auction-user-stat-${item.label}`}
                    sx={{
                      px: 1.1,
                      py: 0.85,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.25,
                      background: idx % 2 === 0 ? "#f8fafc" : "#ffffff",
                      borderBottom: idx === statsRows.length - 1 ? "none" : "1px solid rgba(226,232,240,0.85)",
                    }}
                  >
                    <Typography sx={{ color: "#475569", fontWeight: 800, fontSize: 13 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: "#0f172a", fontWeight: 900, fontSize: 13 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={auctionUserDetailsBusy}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
